import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import { Transition } from '@headlessui/react';

import { useSparksAgentRequestContext } from '@/components/composite/SparksAgent/hooks/useSparksAgentRequestContext';
import { useSparksAgentResize } from '@/components/composite/SparksAgent/hooks/useSparksAgentResize';
import { useSparksAgentShell } from '@/components/composite/SparksAgent/hooks/useSparksAgentShell';
import { useToastsContext } from '@/utils/hooks/context/toast/useToastsContext';
import {
    createAiConversation,
    sendAiMessage,
    useAiConversationMessages,
    useAiConversations
} from '@/utils/hooks/swr/ai';
import { useGlobalAiQuota } from '@/utils/hooks/swr/quotas/useGlobalAiQuota';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { classNames } from '@/utils/classNames';

import type { AiMessage, SparksMessage } from '@/components/composite/SparksAgent/types';

import {
    AI_CONVERSATION_MESSAGES_PAGE_SIZE,
    CONTAINER_WIDTH
} from '@/components/composite/SparksAgent/const';

import {
    SparksAgentService,
    SparksConversation,
    SparksFAB,
    SparksFooter,
    SparksHeader,
    SparksInput,
    SparksWelcome
} from '@/components/composite/SparksAgent';

export function SparksAgent() {
    const { t } = useTranslation('sparks');
    const { toastError } = useToastsContext();

    const [inputValue, setInputValue] = useState('');
    const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [typewriterMessageId, setTypewriterMessageId] = useState<string | null>(null);

    const clearTypewriter = useCallback(() => setTypewriterMessageId(null), []);

    const { isOpen, isAikoOpen, handleOpen, handleMinimize, onNeedSupportClick } =
        useSparksAgentShell({ onMinimize: clearTypewriter });

    const { getContext } = useSparksAgentRequestContext();

    const { containerRef, containerHeight, isResizing, handleResizeMouseDown } =
        useSparksAgentResize();

    const inputRef = useRef<HTMLTextAreaElement>(null);
    const hasResolvedInitialConversation = useRef(false);

    const {
        conversations,
        isLoading: isLoadingConversations,
        mutate: mutateConversations
    } = useAiConversations({ shouldFetch: isOpen });

    const { aiQuota, isLoadingGlobalQuota, isQuotaExhausted, mutateGlobalQuota } = useGlobalAiQuota(
        {
            shouldFetch: isOpen
        }
    );

    const {
        messages: apiMessages,
        isLoading: isLoadingMessages,
        mutate: mutateMessages,
        hasMore,
        loadMore,
        isLoadingMore
    } = useAiConversationMessages({
        conversationId: currentConversationId,
        shouldFetch: Boolean(currentConversationId)
    });

    const mutateMessagesRef = useRef(mutateMessages);
    mutateMessagesRef.current = mutateMessages;

    const messages: SparksMessage[] = apiMessages.map((message) =>
        SparksAgentService.toSparksMessage(message)
    );
    const conversationHasMessages = messages.length > 0;

    useEffect(() => {
        if (!isOpen || isLoadingConversations || hasResolvedInitialConversation.current) return;
        if (conversations.length > 0) {
            setCurrentConversationId(conversations[0].id);
        }

        hasResolvedInitialConversation.current = true;
    }, [isOpen, isLoadingConversations, conversations.length]);

    useEffect(() => {
        setTypewriterMessageId(null);
    }, [currentConversationId]);

    const handleTypewriterComplete = useCallback((messageId: string) => {
        setTypewriterMessageId((current) => (current === messageId ? null : current));
    }, []);

    const handleNewChat = useCallback(async () => {
        setInputValue('');
        setTypewriterMessageId(null);
        try {
            const response = await createAiConversation();
            setCurrentConversationId(response.id);
            void mutateMessages();
            void mutateConversations();
        } catch {
            toastError({ title: t('error_start_conversation') });
        }
    }, [mutateMessages, mutateConversations, t, toastError]);

    const getConversationId = useCallback(async () => {
        if (currentConversationId) return currentConversationId;

        try {
            const response = await createAiConversation();
            flushSync(() => setCurrentConversationId(response.id));
            return response.id;
        } catch {
            return null;
        }
    }, [currentConversationId]);

    const handleSend = useCallback(
        async (textOverride?: string, options?: { retryMessageId?: string }) => {
            const trimmed = (textOverride ?? inputValue ?? '').trim();

            const conversationId = await getConversationId();

            if (!trimmed || !conversationId || isSending) return;

            const optimisticMsg = SparksAgentService.createOptimisticUserMessage(trimmed);
            const shouldAppendOptimisticMessage = !options?.retryMessageId;

            if (textOverride === undefined) {
                setInputValue('');
            }
            setError(null);
            setIsSending(true);

            try {
                if (shouldAppendOptimisticMessage) {
                    await mutateMessagesRef.current(
                        (current) =>
                            SparksAgentService.appendOptimisticToInfiniteData({
                                pages: current,
                                message: optimisticMsg,
                                perPage: AI_CONVERSATION_MESSAGES_PAGE_SIZE
                            }),
                        { revalidate: false }
                    );
                }

                const { data: outputMessage, traceId } = await sendAiMessage(conversationId, {
                    input: trimmed,
                    context: getContext()
                });

                const serverMessage: AiMessage =
                    traceId != null && traceId !== ''
                        ? { ...outputMessage, langfuse_trace_id: traceId }
                        : outputMessage;

                await mutateMessagesRef.current(
                    (current) =>
                        SparksAgentService.appendAIOutputToInfiniteData({
                            pages: current,
                            serverMessage
                        }),
                    { revalidate: false }
                );

                void mutateGlobalQuota();

                setTypewriterMessageId(outputMessage.id);
            } catch {
                setError(t('error_something_wrong'));
                toastError({ title: t('error_send_message') });
            } finally {
                setIsSending(false);
            }
        },
        [inputValue, isSending, getConversationId, t, toastError, getContext, mutateGlobalQuota]
    );

    const handleRetry = useCallback(() => {
        const pending = SparksAgentService.findNewestPendingOptimisticUserMessage(apiMessages);
        const retryText =
            pending?.role === 'user'
                ? SparksAgentService.getAiMessageText(pending).trim()
                : inputValue.trim();
        if (!retryText) return;
        setError(null);
        void handleSend(retryText, { retryMessageId: pending?.id });
    }, [apiMessages, handleSend, inputValue]);

    const renderContainerContent = () => (
        <>
            <SparksHeader onMinimize={handleMinimize} onNewChat={handleNewChat} />
            {conversationHasMessages ? (
                <SparksConversation
                    messages={messages}
                    isLoading={isLoadingMessages}
                    isSending={isSending}
                    hasMore={hasMore}
                    loadMore={loadMore}
                    isLoadingMore={isLoadingMore}
                    error={error}
                    onRetry={handleRetry}
                    typewriterMessageId={typewriterMessageId}
                    onTypewriterComplete={handleTypewriterComplete}
                />
            ) : (
                <SparksWelcome onActionClick={handleSend} isQuotaExhausted={isQuotaExhausted} />
            )}
            <SparksInput
                ref={inputRef}
                value={inputValue}
                onChange={setInputValue}
                onSend={handleSend}
                disabled={isSending || isLoadingConversations}
                aiQuota={aiQuota}
                isQuotaLoading={isLoadingGlobalQuota}
            />
            <SparksFooter onNeedSupportClick={onNeedSupportClick} />
        </>
    );

    return (
        <>
            {/* FAB toggle - visible when Agent is closed and Aiko is closed */}
            <div
                className={classNames('fixed bottom-4 right-4 z-50', {
                    'pointer-events-none opacity-0': isOpen || isAikoOpen
                })}>
                <SparksFAB onClick={handleOpen} />
            </div>

            {/* Desktop floating container */}
            <Transition
                show={isOpen}
                enter="transition-all duration-300 ease-out"
                enterFrom="opacity-0 translate-y-4 scale-95"
                enterTo="opacity-100 translate-y-0 scale-100"
                leave="transition-all duration-200 ease-in"
                leaveFrom="opacity-100 translate-y-0 scale-100"
                leaveTo="opacity-0 translate-y-4 scale-95">
                <div
                    ref={containerRef}
                    className={classNames(
                        'fixed bottom-4 right-4 z-50',
                        'hidden min-h-0 flex-col overflow-hidden rounded-xl border border-icoGray-300 bg-white shadow-icoXS',
                        'dark:border-icoDarkMode-beton dark:bg-icoDarkMode-deepBlack',
                        'md:flex'
                    )}
                    style={{
                        width: CONTAINER_WIDTH,
                        height: containerHeight
                    }}>
                    {/* Resize handle at top edge */}
                    <div
                        onMouseDown={handleResizeMouseDown}
                        className={classNames(
                            'absolute left-0 right-0 top-0 z-10 h-1.5 cursor-ns-resize',
                            'opacity-0 transition-opacity hover:opacity-100',
                            { 'opacity-100': isResizing }
                        )}
                    />
                    {renderContainerContent()}
                </div>
            </Transition>

            {/* Mobile container */}
            <Transition
                show={isOpen}
                enter="transition-all duration-300 ease-out"
                enterFrom="opacity-0 translate-y-full"
                enterTo="opacity-100 translate-y-0"
                leave="transition-all duration-200 ease-in"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-full">
                <div
                    className={classNames(
                        'fixed inset-x-3 inset-y-3 z-50 flex min-h-0 flex-col overflow-hidden rounded-xl border border-icoGray-300 bg-white shadow-icoXS',
                        'dark:border-icoDarkMode-beton dark:bg-icoDarkMode-deepBlack',
                        'md:hidden'
                    )}>
                    {renderContainerContent()}
                </div>
            </Transition>
        </>
    );
}
